let desktopInstance = null;
let mobileInstance = null;

class ScrollEventManager {
  constructor(isMobile = false) {
    this.isMobile = isMobile;
    this.isInitialized = false;

    // Maps of handlerId -> handler
    this.criticalHandlers = new Map();
    this.moderateHandlers = new Map();
    this.heavyHandlers = new Map();

    // Throttling
    this.lastCriticalExecution = 0;
    this.lastModerateExecution = 0;
    this.lastHeavyExecution = 0;

    this.criticalScheduled = false;
    this.moderateScheduled = false;
    this.heavyScheduled = false;

    // Scroll data
    this.sharedScrollData = {};
    this.deviceConfig = this.getDeviceConfig();
    this.lastCallTime = 0;
    this.latestScrollData = null;

    // Unique ID counter
    this.uniqueIdCounter = 0;

    // rAF optimization
    this.queuedAnimationFrame = false;
    this.boundRafScrollHandler = this.rafScrollHandler.bind(this);
  }

  getDeviceConfig() {
    return this.isMobile
      ? { criticalThrottle: 16, moderateThrottle: 200, heavyThrottle: 500 }
      : { criticalThrottle: 16, moderateThrottle: 200, heavyThrottle: 500 };
  }

  /** Generate unique IDs for handlers */
  getUniqueId(prefix = 'scroll-handler') {
    this.uniqueIdCounter += 1;
    return `${prefix}-${Date.now()}-${this.uniqueIdCounter}`;
  }

  /** Initialize scroll listener */
  init() {
    if (this.isInitialized) return;
    this.updateSharedScrollData();
    window.addEventListener('scroll', this.boundRafScrollHandler, {
      passive: true,
    });
    this.isInitialized = true;
    // console.log(`${this.isMobile ? 'Mobile' : 'Desktop'} scroll listener initialized`);
  }

  /** rAF for smooth handling */
  rafScrollHandler() {
    if (this.queuedAnimationFrame) return;
    this.queuedAnimationFrame = true;
    requestAnimationFrame(() => {
      this.handleMasterScroll();
      this.queuedAnimationFrame = false;
    });
  }

  handleMasterScroll() {
    const now = performance.now();
    this.updateSharedScrollData();
    this.latestScrollData = { ...this.sharedScrollData };
    this.scheduleHandlersIfNeeded(now);
    this.lastCallTime = now;
  }

  /** Collect scroll metrics */
  updateSharedScrollData() {
    const now = performance.now();
    const prevScrollTop = this.sharedScrollData.scrollTop || 0;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;
    const scrollDelta = scrollTop - prevScrollTop;

    this.sharedScrollData = {
      scrollTop,
      scrollLeft,
      scrollY: scrollTop,
      scrollX: scrollLeft,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      documentHeight: Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
      ),
      timestamp: now,
      lastScrollTop: prevScrollTop,
      scrollDelta: Math.abs(scrollDelta),
      scrollDirection: scrollDelta > 0 ? 'down' : 'up',
      isScrollingDown: scrollDelta > 0,
      scrollPercentage: Math.min(
        scrollTop /
          (document.documentElement.scrollHeight - window.innerHeight) *
          100,
        100,
      ),
      velocity: Math.abs(scrollDelta) / (now - (this.lastCallTime || now)) || 0,
      deviceConfig: this.deviceConfig,
      isMobile: this.isMobile,
      nearBottom:
        scrollTop >
        0.8 * (document.documentElement.scrollHeight - window.innerHeight),
      nearTop: scrollTop < 100,
      scrolledBeyondFold: scrollTop > window.innerHeight,
    };
  }

  /** Schedule throttled handlers */
  scheduleHandlersIfNeeded(now) {
    if (
      !this.criticalScheduled &&
      now - this.lastCriticalExecution >= this.deviceConfig.criticalThrottle
    ) {
      this.scheduleCriticalHandlers();
    }
    if (
      !this.moderateScheduled &&
      now - this.lastModerateExecution >= this.deviceConfig.moderateThrottle
    ) {
      this.scheduleModerateHandlers();
    }
    if (
      !this.heavyScheduled &&
      now - this.lastHeavyExecution >= this.deviceConfig.heavyThrottle
    ) {
      this.scheduleHeavyHandlers();
    }
  }

  scheduleCriticalHandlers() {
    this.criticalScheduled = true;
    ScrollEventManager.executeHandlers(
      this.criticalHandlers,
      this.latestScrollData,
    );
    this.lastCriticalExecution = performance.now();
    this.criticalScheduled = false;
  }

  scheduleModerateHandlers() {
    this.moderateScheduled = true;
    ScrollEventManager.executeHandlers(
      this.moderateHandlers,
      this.latestScrollData,
    );
    this.lastModerateExecution = performance.now();
    this.moderateScheduled = false;
  }

  scheduleHeavyHandlers() {
    this.heavyScheduled = true;
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deadline => {
        this.executeHeavyHandlers(this.latestScrollData, deadline);
        this.lastHeavyExecution = performance.now();
        this.heavyScheduled = false;
      });
    } else {
      setTimeout(() => {
        this.executeHeavyHandlers(this.latestScrollData);
        this.lastHeavyExecution = performance.now();
        this.heavyScheduled = false;
      }, 0);
    }
  }

  /** Execute handlers */
  static executeHandlers(map, scrollData) {
    map.forEach((handler, id) => {
      try {
        handler.callback(scrollData, handler.config);
      } catch (error) {
        console.warn(`Handler ${id} error:`, error);
      }
    });
  }

  executeHeavyHandlers(scrollData, deadline) {
    this.heavyHandlers.forEach((handler, id) => {
      if (
        !deadline ||
        (deadline.timeRemaining && deadline.timeRemaining() > 1)
      ) {
        try {
          handler.callback(scrollData, handler.config);
        } catch (error) {
          console.warn(`Heavy handler ${id} error:`, error);
        }
      }
    });
  }

  /** Register / Unregister Handlers */
  addCriticalHandlerId(id, callback, config = {}) {
    this.criticalHandlers.set(id, { callback, config });
    return this;
  }

  unregisterCriticalHandler(id) {
    const removed = this.criticalHandlers.delete(id);
    // if (removed) console.log(`===> ScrollEventManager: check Unregistered Critical Handler: ${id}`);
    return removed;
  }

  addModerateHandlerId(id, callback, config = {}) {
    this.moderateHandlers.set(id, { callback, config });
    return this;
  }

  unregisterModerateHandler(id) {
    const removed = this.moderateHandlers.delete(id);
    // if (removed) console.log(`===> ScrollEventManager: check Unregistered Moderate Handler: ${id}`);
    return removed;
  }

  addHeavyHandlerId(id, callback, config = {}) {
    this.heavyHandlers.set(id, { callback, config });
    return this;
  }

  unregisterHeavyHandler(id) {
    const removed = this.heavyHandlers.delete(id);
    return removed;
  }

  /** One-shot Add Handlers */
  registerCriticalHandler(callback, config = {}, prefix = 'ch') {
    if (!this.isInitialized) this.init();
    const handlerId = this.getUniqueId(prefix);
    this.addCriticalHandlerId(handlerId, callback, config);
    return handlerId;
  }

  registerModerateHandler(callback, config = {}, prefix = 'mh') {
    if (!this.isInitialized) this.init();
    const handlerId = this.getUniqueId(prefix);
    this.addModerateHandlerId(handlerId, callback, config);
    return handlerId;
  }

  registerHeavyHandler(callback, config = {}, prefix = 'hh') {
    if (!this.isInitialized) this.init();
    const handlerId = this.getUniqueId(prefix);
    this.addHeavyHandlerId(handlerId, callback, config);
    return handlerId;
  }

  /** Destroy manager */
  destroy() {
    this.criticalScheduled = false;
    this.moderateScheduled = false;
    this.heavyScheduled = false;

    window.removeEventListener('scroll', this.boundRafScrollHandler);

    this.criticalHandlers.clear();
    this.moderateHandlers.clear();
    this.heavyHandlers.clear();

    this.isInitialized = false;
  }
}

/** Singleton helpers */
export const getDesktopScrollManager = () => {
  if (!desktopInstance) desktopInstance = new ScrollEventManager(false);
  return desktopInstance;
};

export const getMobileScrollManager = () => {
  if (!mobileInstance) mobileInstance = new ScrollEventManager(true);
  return mobileInstance;
};

export const getScrollManager = (isMobile = false) =>
  isMobile ? getMobileScrollManager() : getDesktopScrollManager();

export const cleanupScrollManagers = () => {
  if (desktopInstance) {
    desktopInstance.destroy();
    desktopInstance = null;
  }
  if (mobileInstance) {
    mobileInstance.destroy();
    mobileInstance = null;
  }
};

export default ScrollEventManager;
